import { defineStore } from 'pinia';
import { store } from '@/app/stores';
import { Student } from '@/app/api/models/student';
import { Teacher } from '@/app/api/models/teacher';
import { Classroom } from '@/app/api/models/classroom';
import { BaseModuleState } from '@/app/services/modules/BaseModuleService';
import { Module } from '@/app/api/models/module';
import {
    getClassroomSession,
    putSessionToggleFocusScreen,
    startSession,
    doneSession,
    getSessionModules,
} from '@/app/api/session';
import { Session } from '@/app/api/models/session';
import { SessionModule } from '@/app/api/models/sessionModule';
import { getSessionModuleResult, getSessionModuleStudentsResult, storeSessionModule } from '@/app/api/sessionModule';
import { goClassroomInPlay, goSessionResult } from '@/app/helpers/routerHelper';
import { LessonBlock } from '@/app/api/models/lessonBlock';
import { Curriculum } from '@/app/api/models/curriculum';
import { SessionModeEnum } from '@/enums/SessionModeEnum';

interface ClassroomState {
    isOngoing: boolean;
    classroom: Classroom | null;
    teacher: Teacher | null;
    students: Student[];
    moduleState: BaseModuleState | null;
    studentStates: {
        [studentId: number]: any;
    };
    module: Module | null;
    curriculum: Curriculum | null;
    time: string;
    session: Session | null;
    sessionStudents: {
        [studentId: number]: string;
    };
    sessionTitle: string;
    sessionLessonBlocks: LessonBlock[];
    sessionModule: SessionModule | null;
    sessionModules: SessionModule[];
    blurStudents: number[];
    isSessionResultUpdate: boolean;
    pendingResultRequestsBySessionModuleId: number[];
    studentResults: {
        [studentId: number]: any;
    };
    isShowStarLayer: boolean; // 칭찬별 레이어 표시 여부
    isFocusScreen: boolean; // 집중 화면 여부
    starsCount: number; // 별 갯수
}

export const useClassroomStore = defineStore('app-classroom', {
    state: (): ClassroomState => ({
        isOngoing: false,
        classroom: null,
        teacher: null,
        students: [],
        moduleState: null,
        studentStates: {},
        module: null,
        curriculum: null,
        time: '',
        session: null,
        sessionTitle: '',
        sessionLessonBlocks: [],
        sessionStudents: {},
        sessionModule: null,
        sessionModules: [],
        studentResults: {},
        isSessionResultUpdate: false,
        pendingResultRequestsBySessionModuleId: [], // 결과 요청 대기 목록
        isShowStarLayer: false,
        isFocusScreen: false,
        blurStudents: [],
        starsCount: 0,
    }),
    getters: {
        getClassroom(): Classroom | null {
            return this.classroom;
        },
        getIsOngoing(): boolean {
            return this.isOngoing;
        },
        getStudents(): Student[] {
            return this.students;
        },
        getTeacher(): Teacher | null {
            return this.teacher;
        },
        getCurriculum(): Curriculum | null {
            return this.curriculum;
        },
        getTime(): string {
            return this.time;
        },
        getModuleState(): BaseModuleState | null {
            return this.moduleState;
        },
        getStudentStates(): { [studentId: number]: any } {
            return this.studentStates;
        },
        getModule(): Module | null {
            return this.module;
        },
        getIsSessionResultUpdate(): boolean {
            return this.isSessionResultUpdate;
        },
        getSession(): Session | null {
            return this.session;
        },
        getSessionTitle(): string {
            return this.sessionTitle;
        },
        getSessionLessonBlocks(): LessonBlock[] {
            return this.sessionLessonBlocks;
        },
        getSessionStudents(): { [studentId: number]: string } {
            return this.sessionStudents;
        },
        getSessionModule(): SessionModule | null {
            return this.sessionModule;
        },
        getSessionModules(): SessionModule[] {
            return this.sessionModules;
        },
        getStudentResults(): { [studentId: number]: any } {
            return this.studentResults;
        },
        getPendingResultRequestsBySessionModuleId(): number[] {
            return this.pendingResultRequestsBySessionModuleId;
        },
        getIsShowStarLayer(): boolean {
            return this.isShowStarLayer;
        },
        getIsFocusScreen(): boolean {
            return this.isFocusScreen;
        },
        getBlurStudents(): number[] {
            return this.blurStudents;
        },
        getStarsCount(): number {
            return this.starsCount;
        },
    },
    actions: {
        setIsOngoing(isOngoing: boolean) {
            this.isOngoing = isOngoing;
        },
        setClassroom(classroom: Classroom | null) {
            this.classroom = classroom;
        },
        setCurriculum(curriculum: Curriculum | null) {
            this.curriculum = curriculum;
        },
        setTime(time: string) {
            this.time = time;
        },
        setStudents(students: Student[]) {
            this.students = students;
        },
        addStudent(student: Student) {
            this.students = [...this.students, student].sort((a, b) => a.id - b.id);
        },
        updateStudent(studentId: number, student: Partial<Student>) {
            this.students = this.students.map((s) => (s.id === studentId ? { ...s, ...student } : s));
        },
        removeStudent(studentId: number) {
            this.blurStudents = this.blurStudents.filter((id) => id !== studentId);
            this.students = this.students.filter((s) => s.id !== studentId);
        },
        setTeacher(teacher: Teacher | null) {
            this.teacher = teacher;
        },
        setModuleState(moduleState: BaseModuleState | null) {
            this.moduleState = moduleState;
        },
        setStudentStates(studentStates: { [studentId: number]: unknown }) {
            this.studentStates = studentStates;
        },
        setStudentState(studentId: number, state: unknown) {
            this.studentStates[studentId] = state;
        },
        setModule(module: Module | null) {
            this.module = module;
        },
        setIsSessionResultUpdate(isSessionResultUpdate: boolean) {
            this.isSessionResultUpdate = isSessionResultUpdate;
        },
        setSession(session: Session | null) {
            this.session = session;
        },
        setSessionTitle(sessionTitle: string) {
            this.sessionTitle = sessionTitle;
        },
        setSessionLessonBlocks(sessionLessonBlocks: LessonBlock[]) {
            this.sessionLessonBlocks = sessionLessonBlocks;
        },
        setSessionStudents(sessionStudents: { [studentId: number]: string }) {
            this.sessionStudents = sessionStudents;
        },
        setSessionStudent(studentId: number, name: string) {
            this.sessionStudents[studentId] = name;
        },
        setSessionModule(sessionModule: SessionModule | null) {
            this.sessionModule = sessionModule;
        },
        setStudentResults(studentResults: { [studentId: number]: any }) {
            this.studentResults = studentResults;
        },
        setStudentResult(studentId: number, result: any) {
            this.studentResults[studentId] = {
                ...this.studentResults[studentId],
                ...result,
            };
        },
        setSessionModules(sessionModules: SessionModule[]) {
            this.sessionModules = sessionModules;
        },
        setBlurStudents(blurStudents: number[]) {
            this.blurStudents = blurStudents;
        },
        addBlurStudent(studentId: number) {
            this.blurStudents = [...this.blurStudents, studentId];
        },
        removeBlurStudent(studentId: number) {
            this.blurStudents = this.blurStudents.filter((id) => id !== studentId);
        },
        setPendingResultRequestsBySessionModuleId(sessionModuleId: number) {
            this.pendingResultRequestsBySessionModuleId = [
                ...this.pendingResultRequestsBySessionModuleId,
                sessionModuleId,
            ];
        },
        clearPendingResultRequests() {
            this.pendingResultRequestsBySessionModuleId = [];
        },
        setIsFocusScreen(isFocusScreen: boolean) {
            this.isFocusScreen = isFocusScreen;
        },
        setIsShowStarLayer(isShowStarLayer: boolean) {
            this.isShowStarLayer = isShowStarLayer;
        },
        setStarsCount(starsCount: number) {
            this.starsCount = starsCount;
        },
        /**
         * 수업 시작
         */
        async startSession(sessionMode: SessionModeEnum = SessionModeEnum.LECTURE) {
            if (!this.classroom) return;

            const { data } = await startSession(this.classroom, sessionMode);
            this.setSession(data.data.session);

            // 접속자 수업 참여
            const students: { [studentId: number]: string } = {};
            this.students.forEach((student) => {
                students[student.id] = student.name;
            });
            this.setSessionStudents(students);
        },

        /**
         * 수업 완료
         */
        async doneSession() {
            if (!this.classroom || !this.session) return;

            await doneSession(this.classroom);
            goSessionResult(this.session);
        },

        async getAnotherStudentResult(myStudentId: number) {
            if (!this.sessionModule) return;

            const { data } = await getSessionModuleStudentsResult(this.sessionModule);

            const currentStudents = this.studentResults;
            const newStudents = data.data.students;

            const updatedStudents = {
                ...currentStudents,
                ...Object.fromEntries(
                    Object.entries(newStudents).filter(([studentId]) => Number(studentId) !== myStudentId),
                ),
            };

            this.setStudentResults(updatedStudents);
        },

        /**
         * 활동 결과 조회
         */
        async setSessionModuleResult(isAllStudents = false) {
            if (!this.sessionModule) return;

            const { data } = await getSessionModuleResult(this.sessionModule, isAllStudents);
            this.setSessionModule(data.data.session_module);
            this.setModule(data.data.module);
            this.setStudentResults(data.data.students);
        },

        /**
         * 활동 결과 학생들 결과만 조회
         */
        async setSessionModuleStudentsResult() {
            if (this.session?.mode === SessionModeEnum.SELF) {
                const { data } = await getSessionModules(this.session);
                this.setSessionModules(data.data.session_modules);
            } else {
                if (!this.sessionModule) return;

                const { data } = await getSessionModuleStudentsResult(this.sessionModule);
                this.setStudentResults(data.data.students);
                this.setStudentStates(data.data.student_states);
            }
        },

        /**
         * 활동 진행 중 수업 정보 조회
         */
        async setActiveSessionData() {
            if (!this.classroom) return;

            const { data: classroomSessionData } = await getClassroomSession(this.classroom);
            this.setSession(classroomSessionData.data.session);
            this.setSessionStudents(classroomSessionData.data.session_students);
            this.setStudentStates(classroomSessionData.data.student_states);
            this.setModule(classroomSessionData.data.module);
        },

        /**
         * 활동 시작
         */
        async startModule(module: Module, params: Record<string, unknown>) {
            try {
                if (!this.classroom) return;
                if (isStartModule) return;
                isStartModule = true;

                await storeSessionModule(
                    this.classroom,
                    module,
                    {
                        ...params,
                    },
                    this.getCurriculum?.id,
                );

                // 활동 시작 후 활동 페이지로 이동
                goClassroomInPlay(this.classroom);
                isStartModule = false;
            } catch (error) {
                isStartModule = false;
            }
        },

        /**
         * 집중 화면 on/off
         */
        async toggleFocusScreen() {
            try {
                if (!this.classroom) return;

                const { data } = await putSessionToggleFocusScreen(this.classroom);
                this.setIsFocusScreen(data.data.is_focus_screen);
            } catch (error) {
                console.error(error);
            }
        },
    },
});

let isStartModule = false;

export function useClassroomStoreWithOut() {
    return useClassroomStore(store);
}
